/**
 * Operation Plan Card
 *
 * Displays the parsed operation plan with intent, description, and confidence.
 */

import React from 'react';
import { Text, Badge, ProgressBar } from '@fluentui/react-components';
import type { ExcelOperationPlan } from '@excel-ai-studio/shared';

interface OperationPlanCardProps {
  plan: ExcelOperationPlan;
}

export const OperationPlanCard: React.FC<OperationPlanCardProps> = ({ plan }) => {
  const confidencePercent = Math.round(plan.confidence * 100);
  const confidenceColor = plan.confidence >= 0.8 ? 'success' : plan.confidence >= 0.6 ? 'warning' : 'danger';

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <Text className="section-title">{plan.title}</Text>
          <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginTop: 4 }}>
            {plan.description}
          </Text>
        </div>
        <Badge appearance="tint" color={confidenceColor} size="large">
          {confidencePercent}%
        </Badge>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 6 }}>
          Confiance
        </Text>
        <ProgressBar value={plan.confidence} />
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        {plan.targetRange && (
          <div>
            <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 4 }}>
              Plage cible
            </Text>
            <Text size={200} style={{ fontFamily: 'monospace', color: 'var(--colorBrandForeground1)' }}>
              {plan.targetRange}
            </Text>
          </div>
        )}

        {plan.targetColumns && plan.targetColumns.length > 0 && (
          <div>
            <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 4 }}>
              Colonnes cibles
            </Text>
            <Text size={200} style={{ fontFamily: 'monospace', color: 'var(--colorBrandForeground1)' }}>
              {plan.targetColumns.join(', ')}
            </Text>
          </div>
        )}
      </div>

      {plan.requiresConfirmation && (
        <div style={{ marginTop: 12, padding: 8, backgroundColor: 'var(--colorNeutralBackground3)', borderRadius: 4 }}>
          <Text size={200} style={{ color: 'var(--text-secondary)' }}>
            ⚠️ Cette opération nécessite votre confirmation avant d'être appliquée.
          </Text>
        </div>
      )}
    </div>
  );
};
